import React from "react";
import { useIdioma } from "stores/idiomaStore";

/**
 * Cuánto queda del plazo del SII para responder una factura de compra.
 *
 * Quien revisa compras pasa la vista por 50 filas de una vez. Un número solo
 * no se lee a esa velocidad; un color sí. La escala va de verde a rojo a
 * medida que se consumen los 8 días, con la lógica de un semáforo: se
 * entiende sin leyenda y sin que nadie la explique.
 *
 * El número va igual, porque el color dice "urgente" pero no dice "cuánto", y
 * al momento de decidir se necesita el dato exacto.
 *
 * Los cortes son los mismos que usa el backend (`plazoSii.js`). Están
 * duplicados a propósito —el navegador no importa módulos del servidor— y si
 * se cambian en un lado hay que cambiarlos en el otro.
 */

export const DIAS_PLAZO_SII = 8;

/**
 * La escala. `hasta` es el máximo de días restantes al que aplica el tono, de
 * más urgente a menos. El orden importa: se toma el primero que calza.
 *
 * Se separa el texto del fondo para que ambos temas tengan contraste
 * suficiente; un verde claro sobre blanco se lee, sobre azul oscuro no.
 */
const ESCALA = [
  {
    hasta: 0, // último día
    texto: "text-red-700 dark:text-red-200",
    fondo: "bg-red-500",
    pista: "bg-red-100 dark:bg-red-400/20",
  },
  {
    hasta: 1,
    texto: "text-red-700 dark:text-red-200",
    fondo: "bg-red-500",
    pista: "bg-red-100 dark:bg-red-400/20",
  },
  {
    hasta: 2,
    texto: "text-orange-700 dark:text-orange-200",
    fondo: "bg-orange-500",
    pista: "bg-orange-100 dark:bg-orange-400/20",
  },
  {
    hasta: 4,
    texto: "text-amber-700 dark:text-amber-200",
    fondo: "bg-amber-500",
    pista: "bg-amber-100 dark:bg-amber-400/20",
  },
  {
    hasta: 6,
    texto: "text-lime-700 dark:text-lime-200",
    fondo: "bg-lime-500",
    pista: "bg-lime-100 dark:bg-lime-400/20",
  },
  {
    hasta: Infinity,
    texto: "text-emerald-700 dark:text-emerald-200",
    fondo: "bg-emerald-500",
    pista: "bg-emerald-100 dark:bg-emerald-400/20",
  },
];

function tonoPara(diasRestantes) {
  return ESCALA.find((t) => diasRestantes <= t.hasta) || ESCALA[ESCALA.length - 1];
}

/**
 * @param {number|null} diasRestantes — días que quedan. 0 es el último día,
 *   negativo significa que el plazo ya venció. `null` = no aplica.
 */
export default function PlazoSiiIndicator({ diasRestantes }) {
  const { t } = useIdioma();

  if (diasRestantes == null) return null;

  // Plazo vencido: no hay cuenta regresiva que mostrar, solo el hecho.
  if (diasRestantes < 0) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-md bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-800 dark:bg-red-400/20 dark:text-red-200"
        title={t("plazo.vencidaAyuda")}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-red-600" aria-hidden="true" />
        {t("plazo.vencida")}
      </span>
    );
  }

  const tono = tonoPara(diasRestantes);
  const consumidos = Math.min(DIAS_PLAZO_SII, Math.max(0, DIAS_PLAZO_SII - diasRestantes));
  // Siempre se pinta algo: una barra vacía se lee como "sin dato", no como
  // "recién llegada".
  const porcentaje = Math.max(8, Math.round((consumidos / DIAS_PLAZO_SII) * 100));

  const etiqueta =
    diasRestantes === 0
      ? t("plazo.ultimoDia")
      : `${diasRestantes} ${diasRestantes === 1 ? t("plazo.dia") : t("plazo.dias")}`;

  return (
    <span
      className="inline-flex w-[74px] flex-col gap-1"
      title={t("plazo.restanteAyuda")}
      aria-label={`${t("plazo.restanteAyuda")} ${etiqueta}`}
    >
      <span className={`text-[11px] font-semibold leading-none tabular-nums ${tono.texto}`}>
        {etiqueta}
      </span>
      <span
        className={`h-1 w-full overflow-hidden rounded-full ${tono.pista}`}
        role="presentation"
      >
        <span
          className={`block h-full rounded-full transition-[width] duration-300 ${tono.fondo}`}
          style={{ width: `${porcentaje}%` }}
        />
      </span>
    </span>
  );
}
